import { all, call, put, takeLatest, takeEvery, select, delay, actionChannel, take } from 'redux-saga/effects';
import { IMAGE_UPLOAD_STATUS, transactionImageActions } from './storeTransactionImageSlice';
import {
    uploadImageToS3,
    transactionMediaConfirm,
    transactionMediaClose,
    transactionMediaDelete,
    transactionMediaPresign,
} from 'services/storeTransactionImageService';
import { selectQueuedImageKeys, selectTransactionBatches } from './storeTransactionImageSelector';
import { base64ToBlob, getImageProps } from 'components/transaction/components/panels/transaction/cameras/utils/storeImage';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';

const MAX_CONCURRENCY = 3;
const MAX_ATTEMPTS = 4;
const BACKOFFS = [2000, 4000, 8000];
const DEBOUNCE_MS = 100;

function* uploadSingleImage(batchIndex, imageIndex, image) {
    if (!image.presignPutUrl) {
        yield put(
            transactionImageActions.setImageStatus({ batchIndex, imageIndex, status: IMAGE_UPLOAD_STATUS.FAILED, error: 'Missing presignPutUrl' })
        );
        return { assetId: null, retryCount: 0, failed: true };
    }
    // released after its transaction closed; a transaction shown again sends the snapshot as a new upload
    if (image.fileProps?.encodedImage === null) {
        yield put(
            transactionImageActions.setImageStatus({
                batchIndex,
                imageIndex,
                status: IMAGE_UPLOAD_STATUS.FAILED,
                error: 'Image data released',
                isFinalFailed: true,
            })
        );
        return { assetId: null, retryCount: 0, failed: true };
    }
    try {
        let success = false,
            error = null;
        let attemptsMade = 0;
        const mimeType = image.metadata.mime || 'image/jpeg';
        const file = new File([base64ToBlob(image.fileProps.encodedImage)], image.fileProps.imageName || 'image.jpg', { type: mimeType });
        for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
            attemptsMade++;
            try {
                yield call(uploadImageToS3, image.presignPutUrl, file);
                success = true;
                break;
            } catch (err) {
                error = err;
                yield put(
                    transactionImageActions.setImageStatus({
                        batchIndex,
                        imageIndex,
                        status: IMAGE_UPLOAD_STATUS.FAILED,
                        error: { code: error.code, message: error.message },
                    })
                );
                if (attempt < MAX_ATTEMPTS - 1) {
                    yield delay(BACKOFFS[attempt]);
                }
            }
        }
        const retryCount = Math.max(0, attemptsMade - 1);
        if (!success) {
            yield put(
                transactionImageActions.setImageStatus({
                    batchIndex,
                    imageIndex,
                    status: IMAGE_UPLOAD_STATUS.FAILED,
                    error: { code: error.code, message: error.message },
                    isFinalFailed: true,
                })
            );
            return { assetId: null, retryCount, failed: true };
        }
        yield put(transactionImageActions.setImageStatus({ batchIndex, imageIndex, status: IMAGE_UPLOAD_STATUS.SAVED }));
        return { assetId: image.assetId, retryCount, failed: false };
    } catch (err) {
        yield put(
            transactionImageActions.setImageStatus({
                batchIndex,
                imageIndex,
                status: IMAGE_UPLOAD_STATUS.FAILED,
                error: { code: err.code, message: err.message },
            })
        );
        return { assetId: null, retryCount: 0, failed: true };
    }
}

function* uploadBatchesQueue() {
    const batches = yield select(selectTransactionBatches);

    const imagesToUpload = [];
    const transactionMetrics = {};

    batches.forEach((batch, batchIndex) => {
        const transactionId = batch.transactionId;
        if (!transactionMetrics[transactionId]) {
            transactionMetrics[transactionId] = {
                totalImages: 0,
                successCount: 0,
                retryCount: 0,
                failedCount: 0,
            };
        }
        batch.images.forEach((img, imageIndex) => {
            if (img.status === IMAGE_UPLOAD_STATUS.UPLOADING && img.presignPutUrl) {
                imagesToUpload.push({ batchIndex, imageIndex, img, transactionId });
                transactionMetrics[transactionId].totalImages++;
            }
        });
    });

    if (imagesToUpload.length === 0) return;

    yield all(
        imagesToUpload.map(({ batchIndex, imageIndex }) =>
            put(transactionImageActions.setImageStatus({ batchIndex, imageIndex, status: IMAGE_UPLOAD_STATUS.IN_FLIGHT }))
        )
    );

    const successfulAssetIds = [];
    const uploadResults = [];

    const chunks = [];
    for (let i = 0; i < imagesToUpload.length; i += MAX_CONCURRENCY) {
        chunks.push(imagesToUpload.slice(i, i + MAX_CONCURRENCY));
    }

    for (const chunk of chunks) {
        const results = yield all(chunk.map(({ batchIndex, imageIndex, img }) => call(uploadSingleImage, batchIndex, imageIndex, img)));
        results.forEach((result, idx) => {
            const { transactionId } = chunk[idx];
            uploadResults.push({ ...result, transactionId });
            if (result.assetId) {
                successfulAssetIds.push(result.assetId);
            }
        });
    }

    uploadResults.forEach(({ retryCount, failed, transactionId }) => {
        if (transactionMetrics[transactionId]) {
            transactionMetrics[transactionId].retryCount += retryCount;
            if (failed) {
                transactionMetrics[transactionId].failedCount++;
            } else {
                transactionMetrics[transactionId].successCount++;
            }
        }
    });

    Object.entries(transactionMetrics).forEach(([transactionId, metrics]) => {
        if (metrics.totalImages > 0) {
            const uploadSuccessRate = ((metrics.successCount / metrics.totalImages) * 100).toFixed(2);
            console.log(
                `[SAVE_TRANSACTION_IMAGE]: transactionId: ${transactionId}, upload_success_rate: ${uploadSuccessRate}%, retry_count: ${metrics.retryCount}, upload_failed_count: ${metrics.failedCount}`
            );
        }
    });

    if (successfulAssetIds.length > 0) {
        try {
            yield call(transactionMediaConfirm, { assetIds: successfulAssetIds });
        } catch (err) {
            console.error('[SAVE_TRANSACTION_IMAGE] Confirm failed for assetIds', successfulAssetIds, err);
        }
    }
}

const getImageKey = (image) => image.id || image.url;

/**
 * Presigns new agent snapshots of a transaction and queues their upload. Snapshots are deduplicated
 * per transaction in the store, so the same snapshot is uploaded once no matter how many components
 * request it (media section, split view) or how often they remount. Keys are marked before the first
 * async step, so two requests for the same snapshots cannot both pass.
 */
export function* uploadRequested({ payload: { transactionId, locationId, locationName, name, images } }) {
    const queuedImageKeys = yield select(selectQueuedImageKeys);
    const queuedForTransaction = queuedImageKeys[transactionId] || {};
    const newImages = images.filter((image) => {
        const key = getImageKey(image);
        return key && !queuedForTransaction[key];
    });
    if (newImages.length === 0) return;

    const keys = newImages.map(getImageKey);
    yield put(transactionImageActions.markImagesQueued({ transactionId, keys }));

    try {
        const imagesProps = yield call(() => Promise.all(newImages.map((image) => getImageProps(image.encodedImage))));
        const batch = {
            transactionId,
            locationId,
            locationName,
            name,
            origin: newImages[0]?.imageCategory === 'CAMERA_SNAPSHOT' ? 'AGENT' : 'ALISA',
            images: newImages.map((image, idx) => ({
                id: getImageKey(image),
                cameraName: image.cameraName,
                fileProps: {
                    encodedImage: image.encodedImage,
                    imageName: image.imageName,
                },
                metadata: imagesProps[idx],
            })),
        };

        const presignPayload = {
            transactionId: batch.transactionId,
            locationId: batch.locationId,
            locationName: batch.locationName,
            name: batch.name,
            origin: batch.origin,
            images: batch.images.map((image) => ({
                mime: image.metadata.mime.replace('image/', ''),
                byteSize: image.metadata.byteSize,
                width: image.metadata.width,
                height: image.metadata.height,
                cameraName: image.cameraName,
            })),
        };

        let presignResponse;
        try {
            presignResponse = yield call(transactionMediaPresign, presignPayload);
        } catch (err) {
            yield put(
                transactionImageActions.appendBatch({
                    ...batch,
                    // never uploaded from this entry (a later request presigns the snapshot again), so it keeps no base64
                    images: batch.images.map((image) => ({
                        ...image,
                        fileProps: { ...image.fileProps, encodedImage: null },
                        status: IMAGE_UPLOAD_STATUS.FAILED,
                        presignPutUrl: null,
                        error: { code: err?.code, message: err?.message },
                    })),
                })
            );
            yield put(transactionImageActions.unmarkImagesQueued({ transactionId, keys }));
            return;
        }

        yield put(
            transactionImageActions.appendBatch({
                ...batch,
                images: batch.images.map((image, idx) => {
                    const presign = presignResponse.presignUrls?.[idx];
                    return {
                        ...image,
                        presignPutUrl: presign?.presignPutUrl || null,
                        assetId: presign?.assetId || null,
                        status: presign?.presignPutUrl ? IMAGE_UPLOAD_STATUS.UPLOADING : IMAGE_UPLOAD_STATUS.FAILED,
                        error: presign?.presignPutUrl ? null : 'No presignPutUrl',
                    };
                }),
            })
        );
    } catch (err) {
        // a worker error would end the root saga, and every other saga with it; a later request tries these again
        console.error(
            `[SAVE_TRANSACTION_IMAGE] uploadRequested(): transactionId=${transactionId} - error queueing snapshots, message=${err?.message}`
        );
        yield put(transactionImageActions.unmarkImagesQueued({ transactionId, keys }));
    }
}

function* onUploadRequested() {
    yield takeEvery(transactionImageActions.uploadRequested.type, uploadRequested);
}

function* onAppendBatch() {
    const channel = yield actionChannel(transactionImageActions.appendBatch.type);
    while (true) {
        yield take(channel);
        yield delay(DEBOUNCE_MS);
        yield call(uploadBatchesQueue);
    }
}

export function* confirmOrDiscardTransaction({ transactionId, action }) {
    const batches = yield select(selectTransactionBatches);
    // only this transaction's snapshots: the store keeps every transaction of the session
    const assetIds = batches
        .filter((batch) => String(batch.transactionId) === String(transactionId))
        .flatMap((batch) => batch.images)
        .filter((img) => img.status === IMAGE_UPLOAD_STATUS.SAVED && img.assetId)
        .map((img) => img.assetId);

    // always called, also without snapshots: the backend gives the transaction's Macaw images (uploaded by the
    // backend, never in these batches) 30 days on submit and 3 days on discard
    try {
        yield call(transactionMediaClose, transactionId, { assetIds, action });
    } catch (err) {
        // 404: the transaction has no images at all
        if (err?.response?.status === 404) return;
        console.error('[SAVE_TRANSACTION_IMAGE] Close failed for transactionId', transactionId, err);
    }
}

function* onConfirmTransaction() {
    yield takeLatest(transactionImageActions.confirmTransaction.type, function* ({ payload }) {
        yield call(confirmOrDiscardTransaction, { transactionId: payload.transactionId, action: 'SUBMIT' });
    });
}

function* onDiscardTransaction() {
    yield takeLatest(transactionImageActions.discardTransaction.type, function* ({ payload }) {
        yield call(confirmOrDiscardTransaction, { transactionId: payload.transactionId, action: 'DISCARD' });
    });
}

function* onRetryImageUpload() {
    yield takeLatest(transactionImageActions.retryImageUpload.type, function* ({ payload }) {
        const batches = yield select(selectTransactionBatches);
        const batch = batches[payload.batchIndex];
        const img = batch?.images[payload.imageIndex];
        const transactionId = batch?.transactionId;

        if (img && transactionId) {
            yield put(
                transactionImageActions.setImageStatus({
                    batchIndex: payload.batchIndex,
                    imageIndex: payload.imageIndex,
                    status: IMAGE_UPLOAD_STATUS.IN_FLIGHT,
                })
            );
            const result = yield call(uploadSingleImage, payload.batchIndex, payload.imageIndex, img);

            const uploadSuccessRate = result.assetId ? '100.00' : '0.00';
            const failedCount = result.failed ? 1 : 0;

            console.log(
                `[SAVE_TRANSACTION_IMAGE]: transactionId: ${transactionId}, upload_success_rate: ${uploadSuccessRate}%, retry_count: ${result.retryCount}, upload_failed_count: ${failedCount}`
            );

            if (result.assetId) {
                try {
                    yield call(transactionMediaConfirm, { assetIds: [result.assetId] });
                } catch (err) {
                    console.error('[SAVE_TRANSACTION_IMAGE] Confirm failed for assetIds', [result.assetId], err);
                }
            }
        }
    });
}

export function* deleteImage({ payload }) {
    const { assetId, transactionId, imageId } = payload;

    if (!assetId) {
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Cannot delete screenshot: missing asset ID',
                options: { variant: 'error' },
            })
        );
        return;
    }

    try {
        yield call(transactionMediaDelete, [assetId]);

        yield put(transactionImageActions.deleteImageSuccess({ assetId, transactionId, imageId }));
        yield put(formControlActions.removeCameraSnapshot({ transactionId, imageId }));
        // Macaw images: the asset is EXPIRED now and is never uploaded again, so it goes from the desk too
        yield put(formControlActions.removeMacawImage({ transactionId, imageId }));

        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Successfully deleted screenshot',
                options: { variant: 'success' },
            })
        );
    } catch (err) {
        yield put(
            snackbarActions.enqueueSnackbar({
                message: 'Failed to delete screenshot! Error: ' + err.message,
                options: { variant: 'error' },
            })
        );
    } finally {
        yield put(transactionImageActions.deleteImageFinished({ assetId }));
    }
}

// takeEvery: deleting a second image right after the first one must not cancel the first delete
function* onDeleteImage() {
    yield takeEvery(transactionImageActions.deleteImage.type, deleteImage);
}

export function* transactionImageSaga() {
    yield all([
        call(onUploadRequested),
        call(onAppendBatch),
        call(onRetryImageUpload),
        call(onConfirmTransaction),
        call(onDiscardTransaction),
        call(onDeleteImage),
    ]);
}
